import React, { useEffect, useState } from "react";
import moment from "moment";
import { LuAward, LuClock, LuFileCheck, LuArrowLeft } from "react-icons/lu";
import {
  getCertificationExams,
  getCertificationExam,
  submitCertificationExam,
  getMyCertificates,
} from "../../Services/certificationService";
import { errorToast } from "../../utils/toasts";

const ExamRunner = ({ examId, onDone, onBack }) => {
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getCertificationExam(examId)
      .then(setExam)
      .catch(() => errorToast("Unable to load exam."))
      .finally(() => setLoading(false));
  }, [examId]);

  const submit = async () => {
    if (!exam) return;
    if (Object.keys(answers).length < (exam.questions?.length || 0)) {
      if (!window.confirm("Some questions are unanswered. Submit anyway?")) return;
    }
    setSubmitting(true);
    try {
      const res = await submitCertificationExam(examId, answers);
      setResult(res);
      onDone?.();
    } catch (e) {
      errorToast(e?.response?.data?.error?.message || "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6">Loading exam…</div>;
  if (!exam) return <div className="p-6">Exam unavailable.</div>;

  if (result) {
    return (
      <div className="max-w-lg mx-auto text-center py-10">
        <div
          className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
            result.passed ? "bg-green-100" : "bg-red-100"
          }`}
        >
          <LuAward size={40} className={result.passed ? "text-green-600" : "text-red-500"} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">
          {result.passed ? "Congratulations!" : "Not this time"}
        </h2>
        <p className="text-gray-500 mt-1">
          You scored {result.score}/{result.total} ({result.percentage}%)
        </p>
        {result.passed && result.certificate && (
          <div className="mt-4 rounded-xl bg-green-50 border border-green-200 p-4">
            <p className="text-green-800 font-medium">
              Certificate issued: {result.certificate.certificate_number}
            </p>
            <p className="text-sm text-green-700">Badge: {result.certificate.badge_label}</p>
          </div>
        )}
        <button
          onClick={onBack}
          className="mt-6 rounded-full bg-[#425F80] text-white px-6 py-2.5"
        >
          Back to Certifications
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-4">
      <button onClick={onBack} className="flex items-center gap-1 text-gray-500 mb-4">
        <LuArrowLeft size={16} /> Back
      </button>
      <h2 className="text-xl font-bold text-gray-800">{exam.title}</h2>
      <p className="text-sm text-gray-500 mb-6">
        Passing score {exam.passing_score}% · {exam.questions?.length || 0} questions
      </p>

      {exam.questions?.map((q, idx) => (
        <div key={q.id} className="mb-6 bg-white rounded-xl border border-gray-100 p-4">
          <p className="font-medium text-gray-800 mb-3">
            {idx + 1}. {q.question}
          </p>
          <div className="space-y-2">
            {(q.options || []).map((opt) => (
              <label
                key={opt.key}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${
                  answers[q.id] === opt.key
                    ? "border-[#425F80] bg-[#425F80]/5"
                    : "border-gray-200"
                }`}
              >
                <input
                  type="radio"
                  name={q.id}
                  checked={answers[q.id] === opt.key}
                  onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt.key }))}
                />
                <span className="text-sm text-gray-700">{opt.text}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      <button
        onClick={submit}
        disabled={submitting}
        className="w-full rounded-full bg-[#425F80] text-white py-3 font-medium disabled:opacity-50"
      >
        {submitting ? "Submitting…" : "Submit Exam"}
      </button>
    </div>
  );
};

const Certification = () => {
  const [exams, setExams] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeExam, setActiveExam] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([getCertificationExams(), getMyCertificates()]);
      setExams(e || []);
      setCerts(c || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const certExamIds = new Set(certs.map((c) => c.exam_id));

  if (activeExam) {
    return (
      <div className="page-container p-6">
        <ExamRunner
          examId={activeExam}
          onDone={load}
          onBack={() => setActiveExam(null)}
        />
      </div>
    );
  }

  return (
    <div className="page-container p-6">
      <div className="flex items-center gap-2 mb-6">
        <LuAward className="text-[#425F80]" size={26} />
        <h1 className="text-2xl font-bold text-gray-800">Certifications</h1>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {exams.length === 0 && <p className="text-gray-400">No exams available.</p>}
            {exams.map((exam) => {
              const earned = certExamIds.has(exam.id);
              return (
                <div
                  key={exam.id}
                  className="rounded-2xl border border-gray-100 shadow-sm p-5 bg-white flex flex-col"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800">{exam.title}</h3>
                    {exam.is_pro_only && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
                        PRO
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1 flex-1">{exam.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-400 my-3">
                    <span className="flex items-center gap-1">
                      <LuClock size={13} /> {exam.duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <LuFileCheck size={13} /> Pass {exam.passing_score}%
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveExam(exam.id)}
                    className={`w-full rounded-full py-2 font-medium ${
                      earned
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-[#425F80] text-white"
                    }`}
                  >
                    {earned ? "Retake" : "Take Exam"}
                  </button>
                </div>
              );
            })}
          </div>

          <h2 className="text-lg font-semibold text-gray-800 mb-4">My Certificates</h2>
          {certs.length === 0 ? (
            <p className="text-gray-400">No certificates earned yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certs.map((c) => (
                <div
                  key={c.id}
                  className="rounded-xl border border-gray-100 p-4 bg-white flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <LuAward className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{c.badge_label}</p>
                    <p className="text-xs text-gray-400">{c.certificate_number}</p>
                    <p className="text-xs text-gray-400">
                      Issued {moment(c.issued_at).format("DD MMM YYYY")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Certification;
